import { useQueryCore } from "@/hooks/useQueryCore";
import { localCache } from "@/lib/cache";
import {
  getPerson,
  getFamily,
  getChurch,
  getPeople,
  getResponsibilities,
  getLastVisitByPerson,
} from "@/lib/api";

export function usePersonDetail(id) {
  const { data, loading, error, refetch } = useQueryCore({
    key: ["person-detail", id],
    enabled: !!id,
    queryFn: async () => {
      const person = await getPerson(id);

      if (!person) {
        return { person: null, family: null, church: null, familyMembers: [], resps: [], lastVisit: null };
      }

      // Fast-path: if the family compound is already in Dexie (written by useFamilyDetail
      // or by a previous PersonDetail visit), use it for family/church/members without
      // hitting Supabase. getFamily still runs in parallel to refresh the family cache;
      // the compound path just skips the church + members roundtrips on this visit.
      const famCompound = person.family_id
        ? await localCache.get(`fam_detail_compound_${person.family_id}`)
        : null;

      const familyPromise = getFamily(person.family_id);

      const churchPromise = famCompound?.church
        ? Promise.resolve(famCompound.church)
        : familyPromise.then((f) => (f?.church_id ? getChurch(f.church_id) : null));

      const membersPromise = famCompound?.people
        ? Promise.resolve(famCompound.people)
        : (person.family_id ? getPeople({ family_id: person.family_id }) : Promise.resolve([]));

      const [family, resps, lastVisit, familyMembers, church] = await Promise.all([
        familyPromise,
        getResponsibilities(id),
        getLastVisitByPerson(id),
        membersPromise,
        churchPromise,
      ]);

      const compound = { person, family, church, familyMembers, resps, lastVisit };
      // Only persist if person resolved — avoids writing null compounds that would
      // cause "not found" flash on the next online visit (root cause C fix).
      if (id && person) localCache.set(`person_detail_${id}`, compound).catch(() => {});
      return compound;
    },
    // Dexie hydration: written explicitly above so cold-start always hits after first visit.
    cacheKey: id ? `person_detail_${id}` : null,
  });

  // useQueryCore initializes data as []. Once the first fetch resolves,
  // data becomes a plain object. Treat the unresolved initial state as loading
  // so "not found" never flashes before the first fetch completes.
  const resolved = !Array.isArray(data);

  return {
    person: data?.person ?? null,
    family: data?.family ?? null,
    church: data?.church ?? null,
    familyMembers: data?.familyMembers ?? [],
    resps: data?.resps ?? [],
    lastVisit: data?.lastVisit ?? null,
    loading: loading || !resolved,
    error,
    refetch,
  };
}
